/**
 * Async Select API Client
 * Shared utilities for fetching options and values for async select components
 *
 * NOTE: All requests go through the app's shared, authenticated `ApiClient`
 * (see `services/api/client.ts`) rather than the browser's native `fetch()`.
 * `ApiClient`'s request interceptor attaches the Azure AD `Authorization`
 * header; calling `fetch()` directly bypassed that entirely and made every
 * async-select dropdown (manufacturers, facilities, grades, therapeutic
 * areas, etc.) 401 on any environment that enforces real auth (stage/prod).
 * Local dev never caught this because the API runs with `DISABLE_AUTH=true`.
 *
 * Callers must pass in the authenticated `ApiClient` instance obtained via
 * `useApi()` — see `shared/hooks/useAsyncSelect.ts`.
 */

import { ApiClient } from '../../services/api/client';
import { buildSimpleFilterQuery } from './advancedSearchQuery';

export interface SelectOption {
  value: string;
  label: string;
  data?: Record<string, unknown>;
}

/**
 * Sentinel convention for a "pending new record" option in a creatable
 * multi-select (row-correction modal Grade/Therapeutic Area redesign):
 * a chip the reviewer added/kept that doesn't correspond to a catalog row
 * yet. Real catalog ids are plain numeric strings, which can never start
 * with this prefix, so the two are unambiguous to tell apart.
 */
export const NEW_OPTION_PREFIX = '__new__';

export function toNewOptionValue(name: string): string {
  return `${NEW_OPTION_PREFIX}${name}`;
}

export function isNewOptionValue(value: string): boolean {
  return value.startsWith(NEW_OPTION_PREFIX);
}

export function newOptionValueToName(value: string): string {
  return value.slice(NEW_OPTION_PREFIX.length);
}

export interface AsyncSelectApiOptions {
  apiEndpoint: string;
  searchField?: string; // Optional - not needed for ID-based fetches
  valueField: string;
  labelField: string;
  /**
   * Fixed query params sent on every request to `apiEndpoint`, alongside
   * (not instead of) the search-term-driven `advancedSearch` param — e.g.
   * `{ isKnownParent: 'true' }` for the manufacturer "Parent" picker
   * (issue #653), which must stay scoped even when the user hasn't typed
   * anything yet.
   */
  extraParams?: Record<string, string>;
}

/**
 * Pickers must keep seeing product-less manufacturers (a product-less
 * manufacturer is how the first product gets linked), so every async-select
 * request to the manufacturers list sends `listMode=lookup` unless the caller
 * set its own `listMode`. Without it the server applies the grid's default view
 * (no archived, at least one product). Docs: manufacturer-restore-and-archive,
 * Decision 12.
 */
export function withManufacturerLookupMode(
  apiEndpoint: string,
  extraParams?: Record<string, string>
): Record<string, string> | undefined {
  const isManufacturers = apiEndpoint.split('?')[0] === '/manufacturers';
  if (!isManufacturers || extraParams?.listMode) {
    return extraParams;
  }
  return { listMode: 'lookup', ...extraParams };
}

export interface FetchOptionsResult {
  options: SelectOption[];
  total: number;
  hasMore: boolean;
}

/**
 * Fetch options for async select with pagination and search
 *
 * @param apiClient - Authenticated ApiClient instance (from `useApi()`)
 * @param options - Configuration for API call
 * @param searchTerm - Optional search term to filter results
 * @param limit - Number of items per page (default: 20)
 * @param offset - Starting offset for pagination (default: 0)
 * @returns Promise with options array, total count, and hasMore flag
 *
 * @example
 * const result = await fetchAsyncSelectOptions(apiClient, {
 *   apiEndpoint: '/grades',
 *   searchField: 'grade_name',
 *   valueField: 'grade_id',
 *   labelField: 'grade_name'
 * }, 'GMP', 20, 0);
 */
export async function fetchAsyncSelectOptions(
  apiClient: ApiClient,
  options: AsyncSelectApiOptions,
  searchTerm?: string,
  limit: number = 20,
  offset: number = 0
): Promise<FetchOptionsResult> {
  const { apiEndpoint, searchField, valueField, labelField, extraParams } =
    options;

  // Validation
  if (!apiEndpoint) {
    console.error('fetchAsyncSelectOptions: apiEndpoint is required');
    return { options: [], total: 0, hasMore: false };
  }

  if (!valueField || !labelField) {
    console.error(
      'fetchAsyncSelectOptions: valueField and labelField are required'
    );
    return { options: [], total: 0, hasMore: false };
  }

  try {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      ...withManufacturerLookupMode(apiEndpoint, extraParams),
    });

    // Add advanced search if search term is provided
    if (searchTerm && searchField) {
      const advancedSearch = buildSimpleFilterQuery(
        searchField,
        'contains',
        searchTerm
      );
      params.append('advancedSearch', JSON.stringify(advancedSearch));
    }

    const url = `${apiEndpoint}?${params.toString()}`;
    const response = await apiClient.get<unknown>(url);
    const data = response.data;

    // Validate response structure
    if (!data || typeof data !== 'object') {
      console.error('Invalid API response format', { apiEndpoint, data });
      return { options: [], total: 0, hasMore: false };
    }

    const dataRecord = data as Record<string, unknown>;

    // Extract items array - handle different response formats
    const items =
      dataRecord.items ||
      dataRecord.data ||
      dataRecord[Object.keys(dataRecord)[0]] ||
      [];

    // Validate items is an array
    if (!Array.isArray(items)) {
      console.error('API response items is not an array', {
        apiEndpoint,
        items,
      });
      return { options: [], total: 0, hasMore: false };
    }

    const total = (dataRecord.total as number | undefined) || items.length;

    // Convert to select options with validation
    const selectOptions: SelectOption[] = items
      .map((item: Record<string, unknown>) => {
        const value = item[valueField];
        const label = item[labelField];

        // Skip items with missing required fields
        if (value === undefined || value === null) {
          console.warn(`Item missing valueField '${valueField}'`, item);
          return null;
        }

        return {
          value: String(value),
          label: String(label) || 'Unnamed',
          data: item,
        } as SelectOption;
      })
      .filter((option): option is SelectOption => option !== null);

    const hasMore = offset + items.length < total;

    return {
      options: selectOptions,
      total,
      hasMore,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error('Error fetching async select options:', {
      error: errorMessage,
      apiEndpoint,
      searchTerm,
    });
    throw error;
  }
}

/**
 * Fetch a single value by ID for displaying selected option
 * Handles the case where backend doesn't support searching by ID field
 *
 * @param apiClient - Authenticated ApiClient instance (from `useApi()`)
 * @param options - Configuration for API call
 * @param id - The ID value to fetch
 * @returns Promise with SelectOption or null if not found
 *
 * @example
 * const option = await fetchAsyncSelectValueById(apiClient, {
 *   apiEndpoint: '/grades',
 *   searchField: 'grade_name',
 *   valueField: 'grade_id',
 *   labelField: 'grade_name'
 * }, '4');
 */
export async function fetchAsyncSelectValueById(
  apiClient: ApiClient,
  options: AsyncSelectApiOptions,
  id: string
): Promise<SelectOption | null> {
  if (!id) return null;

  const { apiEndpoint, valueField, labelField } = options;

  try {
    const params = new URLSearchParams({
      limit: '1',
      offset: '0',
    });

    // Fetch by exact match on value field (which is the ID field in modal context)
    const advancedSearch = buildSimpleFilterQuery(valueField, 'equals', id);
    params.append('advancedSearch', JSON.stringify(advancedSearch));

    const url = `${apiEndpoint}?${params.toString()}`;
    const response = await apiClient.get<unknown>(url);
    const data = response.data;

    // Validate response structure (mirrors fetchAsyncSelectOptions) — guards
    // against a non-object or empty body (e.g. an unexpected 204) before
    // calling Object.keys() on it.
    if (!data || typeof data !== 'object') {
      console.error('Invalid API response format', { apiEndpoint, data });
      return { value: id, label: id };
    }

    const dataRecord = data as Record<string, unknown>;
    const items = (dataRecord.items ||
      dataRecord.data ||
      dataRecord[Object.keys(dataRecord)[0]] ||
      []) as unknown[];

    if (Array.isArray(items) && items.length > 0) {
      const item = items[0] as Record<string, unknown>;
      return {
        value: String(item[valueField]),
        label: (item[labelField] as string) || 'Unnamed',
        data: item,
      };
    }

    // If no items found, show the value as the label
    return { value: id, label: id };
  } catch (error) {
    // ApiClient throws on any non-2xx (unlike raw fetch(), which only
    // rejected on network failure) — a 404 here just means "not found",
    // which is routine and already handled by the fallback below. Only log
    // genuine network failures, mirroring the pattern in useAsyncSelect.ts.
    const code = (error as { code?: string })?.code;
    if (code === 'NETWORK_ERROR') {
      console.error('Network error fetching value by ID:', error);
    }
    return { value: id, label: id };
  }
}

export type NameResolutionResult =
  | { status: 'matched'; option: SelectOption }
  | { status: 'unmatched' }
  | { status: 'ambiguous' };

/**
 * Resolve a raw upload-sheet name to an existing record via an exact-match
 * (`equals`) lookup on the name field -- Architecture Decision 5 (PUSI-004
 * Sub-task 6.2.2). Deliberately NOT the typeahead's `'contains'` search
 * (`fetchAsyncSelectOptions`), which sorts alphabetically and caps at a
 * page of results -- a genuine exact match can sort outside that page and
 * be wrongly reported as unmatched. Fetches up to 2 results: 0 is
 * unmatched, 1 is a clean match, 2+ is ambiguous (neither Grades nor
 * therapeutic_areas has a unique constraint on the name column) -- treated
 * distinctly from unmatched since the select only ever displays the name
 * text, so a reviewer asked to pick between visually-identical duplicate
 * records would be guessing, not choosing.
 *
 * @param apiClient - Authenticated ApiClient instance (from `useApi()`)
 * @param options - Configuration for API call (searchField is the name
 *   column to match on, e.g. `grade_name`)
 * @param name - The raw name to resolve (already trimmed)
 * @returns Promise<NameResolutionResult>
 */
export async function fetchAsyncSelectValueByName(
  apiClient: ApiClient,
  options: AsyncSelectApiOptions,
  name: string
): Promise<NameResolutionResult> {
  const { apiEndpoint, searchField, valueField, labelField } = options;
  if (!searchField) {
    return { status: 'unmatched' };
  }

  // No try/catch here -- a genuine network/API failure propagates to the
  // caller (useExactNameResolution), which is what distinguishes it from
  // "unmatched" (a retryable error state, not the unmatched hint).
  const params = new URLSearchParams({
    limit: '2',
    offset: '0',
    ...withManufacturerLookupMode(apiEndpoint, options.extraParams),
  });
  const advancedSearch = buildSimpleFilterQuery(searchField, 'equals', name);
  params.append('advancedSearch', JSON.stringify(advancedSearch));

  const url = `${apiEndpoint}?${params.toString()}`;
  const response = await apiClient.get<unknown>(url);
  const data = response.data;

  if (!data || typeof data !== 'object') {
    return { status: 'unmatched' };
  }

  const dataRecord = data as Record<string, unknown>;
  const items = (dataRecord.items ||
    dataRecord.data ||
    dataRecord[Object.keys(dataRecord)[0]] ||
    []) as unknown[];

  if (!Array.isArray(items) || items.length === 0) {
    return { status: 'unmatched' };
  }
  if (items.length > 1) {
    return { status: 'ambiguous' };
  }

  const item = items[0] as Record<string, unknown>;
  return {
    status: 'matched',
    option: {
      value: String(item[valueField]),
      label: (item[labelField] as string) || 'Unnamed',
      data: item,
    },
  };
}

/**
 * Fetch multiple values by IDs for displaying selected options
 * Useful for multi-select components
 *
 * @param apiClient - Authenticated ApiClient instance (from `useApi()`)
 * @param options - Configuration for API call
 * @param ids - Array of ID values to fetch
 * @returns Promise with array of SelectOptions
 */
export async function fetchAsyncSelectValuesByIds(
  apiClient: ApiClient,
  options: AsyncSelectApiOptions,
  ids: string[]
): Promise<SelectOption[]> {
  if (!ids || ids.length === 0) return [];

  // Fetch all IDs concurrently
  const promises = ids.map(id =>
    fetchAsyncSelectValueById(apiClient, options, id)
  );
  const results = await Promise.all(promises);

  // Filter out null results
  return results.filter((option): option is SelectOption => option !== null);
}

/**
 * Fetch multiple values by IDs in a single request via `${apiEndpoint}/batch`.
 * Use for endpoints that support batch label resolution (see
 * BATCH_ENDPOINTS in useAsyncSelect.ts) -- one request instead of one per id,
 * to avoid tripping rate limiting when a relation has many selected values.
 *
 * @param apiClient - Authenticated ApiClient instance (from `useApi()`)
 * @param options - Configuration for API call
 * @param ids - Array of ID values to fetch
 * @returns Promise with array of SelectOptions for the ids that were found
 *   (ids not found in the response are simply omitted -- useAsyncSelect's
 *   fetchSelectedValues fills those in with a literal {value: id, label: id}
 *   fallback rather than retrying via fetchAsyncSelectValuesByIds)
 */
export async function fetchAsyncSelectValuesBatch(
  apiClient: ApiClient,
  options: AsyncSelectApiOptions,
  ids: string[]
): Promise<SelectOption[]> {
  if (!ids || ids.length === 0) return [];

  const { apiEndpoint, valueField, labelField } = options;

  // ApiClient throws on any non-2xx (unlike raw fetch(), which required an
  // explicit response.ok check) -- the caller (useAsyncSelect's
  // fetchSelectedValues) already catches and falls back on failure.
  const response = await apiClient.get<unknown>(
    `${apiEndpoint}/batch?ids=${ids.map(encodeURIComponent).join(',')}`
  );
  const data = response.data;

  if (!data || typeof data !== 'object') {
    console.error('Invalid API response format', { apiEndpoint, data });
    return [];
  }

  const dataRecord = data as Record<string, unknown>;
  const items = (dataRecord.items ||
    dataRecord.data ||
    dataRecord[Object.keys(dataRecord)[0]] ||
    []) as unknown[];

  if (!Array.isArray(items)) {
    console.error('API response items is not an array', {
      apiEndpoint,
      items,
    });
    return [];
  }

  return items.map(rawItem => {
    const item = rawItem as Record<string, unknown>;
    return {
      value: String(item[valueField]),
      label: (item[labelField] as string) || 'Unnamed',
      data: item,
    };
  });
}
